import { getDayKey } from '../scheduleHelpers';
import {
  MONTHLY_HEADER_HEIGHT,
  MONTHLY_ENTRY_PADDING,
} from '../schedule.constants';

/*
  Used to prevent infinite loop. Realistically lane limit is closer to 4-5
*/
const LANE_LIMIT = 10;

/**
 * Assigns stable vertical lanes for multi-day shifts across a week so bars line up
 * between columns (see HARBOUR-6791). Each day then renders multi-day rows in lane order,
 * inserting spacers for empty lanes so spanning bars do not collide with rows from
 * neighbouring days.
 *
 * @param {Object} multiDayPerDay - Multi-day shifts keyed by getDayKey(day)
 * @param {import('luxon').DateTime[]} weekDays - Seven days of the row (Luxon)
 * @param {number} [height] - Measured week row height (same source as MonthWeek → MonthDay)
 * @returns {{ laneByShiftId: Map<number, number> }}
 */
export const positionWeekShifts = ({
  multiDayPerDay,
  weekDays = [],
  height,
}) => {
  const h = Number.isFinite(height) ? height : 0;
  const layoutHeight = h - MONTHLY_ENTRY_PADDING;
  if (layoutHeight < MONTHLY_HEADER_HEIGHT) {
    return { laneByShiftId: new Map() };
  }

  const laneByShiftId = new Map();

  const uniqueMultiDay = {};
  const maxLaneByDay = {};
  weekDays.forEach((day) => {
    const dayKey = getDayKey(day);
    const multiDay = multiDayPerDay[dayKey] ?? [];
    multiDay.forEach((shift) => {
      if (!uniqueMultiDay[shift.id]) {
        uniqueMultiDay[shift.id] = {
          shift,
          days: [],
        };
      }
      uniqueMultiDay[shift.id].days.push(dayKey);
    });
  });

  const uniqueMultiArr = Object.values(uniqueMultiDay);
  uniqueMultiArr.sort((a, b) => b.days.length - a.days.length);
  uniqueMultiArr.forEach(({ shift, days }) => {
    let myLane = 0;
    while (myLane < LANE_LIMIT) {
      // eslint-disable-next-line no-loop-func
      const isAvailable = days.every((dayKey) => (
        !maxLaneByDay[dayKey]?.has(myLane)
      ));
      if (isAvailable) {
        laneByShiftId.set(shift.id, myLane);
        // eslint-disable-next-line no-loop-func
        days.forEach((dayKey) => {
          if (!maxLaneByDay[dayKey]) {
            maxLaneByDay[dayKey] = new Set();
          }
          maxLaneByDay[dayKey].add(myLane);
        });
        break;
      }
      myLane += 1;
    }
  });

  return { laneByShiftId };
};

export const buildLaneOrderedMultiDay = (multiDay, laneByShiftId) => {
  if (!laneByShiftId || laneByShiftId.size === 0) {
    return null;
  }
  const uniqueMulti = [...new Map(multiDay.map((s) => [s.id, s])).values()];
  let maxL = -1;
  uniqueMulti.forEach((s) => {
    const l = laneByShiftId.get(s.id);
    if (l != null) maxL = Math.max(maxL, l);
  });
  if (maxL < 0) return null;

  const slots = Array(maxL + 1).fill(null);
  uniqueMulti.forEach((s) => {
    const l = laneByShiftId.get(s.id);
    if (l != null && l >= 0 && l <= maxL) {
      slots[l] = s;
    }
  });

  const ordered = [];
  for (let i = 0; i < slots.length; i += 1) {
    if (slots[i]) {
      ordered.push(slots[i]);
    } else {
      ordered.push({ __laneSpacer: true, id: `lane-spacer-${i}` });
    }
  }
  uniqueMulti.forEach((s) => {
    if (!laneByShiftId.has(s.id)) ordered.push(s);
  });
  return ordered;
};
